import { useCallback, useEffect, useState } from 'react';

import {
  checkFinancialBlockage,
  getCreditsSummary,
  getRedirectToken,
  startProcess,
} from '../../api/requests/tuitionContinuityV2';
import Loading from '../../components/info/Loading';
import {
  ContinuityEventStatus,
  ContinuityStatusResponse,
  CreditsSummaryResponse,
} from '../../types/tuitionContinuityV2';
import CreditsSummary from './parts/CreditsSummary';
import EventStatusMessage from './parts/EventStatusMessage';
import FinancialBlockageModal from './parts/FinancialBlockageModal';
import ReliquidationCourses from './parts/ReliquidationCourses';
import ReliquidationWarningModal from './parts/ReliquidationWarningModal';

export function TuitionContinuityV2Content({
  studyPlanEnrollmentId,
  statusData,
}: {
  studyPlanEnrollmentId: number;
  statusData: ContinuityStatusResponse;
}) {
  const [
    creditsSummaryData,
    setCreditsSummaryData,
  ] = useState<CreditsSummaryResponse | null>(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [showReliquidationModal, setShowReliquidationModal] = useState(false);
  const [showBlockageModal, setShowBlockageModal] = useState(false);

  const loadCredits = useCallback(async () => {
    if (statusData.eventStatus !== ContinuityEventStatus.ACTIVE) return;
    setLoadingCredits(true);
    const creditsRes = await getCreditsSummary(studyPlanEnrollmentId, 0);
    if (creditsRes.data) {
      setCreditsSummaryData(creditsRes.data);
    }
    setLoadingCredits(false);
  }, [studyPlanEnrollmentId, statusData.eventStatus]);

  useEffect(() => {
    loadCredits();
  }, [loadCredits]);

  const navigateToSteps = useCallback(async () => {
    if (!statusData.academicCalendarId) {
      return;
    }
    // Ensure process exists before redirecting
    let processId = statusData.processId;
    if (!statusData.hasActiveProcess) {
      const startRes = await startProcess(
        studyPlanEnrollmentId,
        statusData.academicCalendarId,
      );
      if (startRes.data) {
        processId = startRes.data.processId;
      }
    }

    if (!processId) {
      return;
    }

    // Get cross-app redirect token and redirect to reformada-online
    const tokenRes = await getRedirectToken();
    if (tokenRes.data?.attemptId) {
      const callbackUrl = encodeURIComponent(
        `/continuity-enrollment/steps/${processId}`,
      );
      const baseUrl = statusData.matriculaOnlineUrl.replace(/\/+$/, '');
      window.open(
        `${baseUrl}/auth/signin/${tokenRes.data.attemptId}?callbackUrl=${callbackUrl}`,
        '_blank',
      );
    }
  }, [statusData, studyPlanEnrollmentId]);

  const handleGoToTuition = useCallback(() => {
    // Check for pending reliquidation
    if (creditsSummaryData?.hasReliquidationRequest) {
      setShowReliquidationModal(true);
      return;
    }

    navigateToSteps();
  }, [creditsSummaryData, navigateToSteps]);

  const handleDownloadVoucher = useCallback(async () => {
    const res = await checkFinancialBlockage(0);
    if (res.data?.blocked) {
      setShowBlockageModal(true);
      return;
    }
    // TODO: Download voucher
  }, []);

  if (
    loadingCredits &&
    statusData.eventStatus === ContinuityEventStatus.ACTIVE
  ) {
    return <Loading insideCard />;
  }

  return (
    <>
      <EventStatusMessage
        status={statusData}
        onGoToTuition={handleGoToTuition}
        onDownloadVoucher={handleDownloadVoucher}
      />

      {statusData.eventStatus === ContinuityEventStatus.ACTIVE &&
        creditsSummaryData && (
          <>
            <CreditsSummary summary={creditsSummaryData.summary} />
            <ReliquidationCourses
              courses={creditsSummaryData.reliquidationCourses}
              totalCredits={creditsSummaryData.summary.creditsToEnroll}
            />
          </>
        )}

      <ReliquidationWarningModal
        isOpen={showReliquidationModal}
        toggle={() => setShowReliquidationModal(false)}
        onContinue={() => {
          setShowReliquidationModal(false);
          navigateToSteps();
        }}
      />

      <FinancialBlockageModal
        isOpen={showBlockageModal}
        toggle={() => setShowBlockageModal(false)}
      />
    </>
  );
}
